import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { Track, getVehicle } from '@voidline/shared';
import { RaceView, ShipView } from '../game/view.js';
import { buildTrackMeshes, TrackMeshHandle } from './trackMesh.js';
import { buildShip, ShipHandle } from './shipFactory.js';
import { ChaseCamera } from './camera.js';
import { ParticleSystem, ShipEffects, SpeedStreaks } from './effects.js';
import { getEnvironment } from './environments/index.js';
import { EnvironmentHandle } from './environments/types.js';
import { Settings } from '../persist.js';

export interface RenderStats {
  fps: number;
  frameMs: number;
  drawCalls: number;
  triangles: number;
  entities: number;
}

/**
 * The 3D presentation layer. Owns the WebGL context, scene graph, post chain and
 * all visual systems; consumes a RaceView every frame.
 */
export class GameRenderer {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  chase: ChaseCamera;
  private composer: EffectComposer | null = null;
  private bloomPass: UnrealBloomPass | null = null;

  private trackHandle: TrackMeshHandle | null = null;
  private environment: EnvironmentHandle | null = null;
  private ships: ShipHandle[] = [];
  private shipFx: ShipEffects[] = [];
  private particles: ParticleSystem | null = null;
  private streaks: SpeedStreaks | null = null;
  private ghostShip: ShipHandle | null = null;
  private currentTrack: Track | null = null;

  private time = 0;
  private frameTimes: number[] = [];
  stats: RenderStats = { fps: 0, frameMs: 0, drawCalls: 0, triangles: 0, entities: 0 };

  constructor(
    canvas: HTMLCanvasElement,
    private settings: Settings
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.graphicsPreset !== 'performance',
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    // manual info reset so draw-call stats cover the whole frame (incl. post passes)
    this.renderer.info.autoReset = false;
    this.scene = new THREE.Scene();
    this.chase = new ChaseCamera(settings);
    this.applyQuality();
    this.resize();
  }

  applyQuality(): void {
    const preset = this.settings.graphicsPreset;
    const scale = this.settings.renderScale;
    const dpr = Math.min(window.devicePixelRatio || 1, preset === 'quality' ? 2 : 1.5);
    this.renderer.setPixelRatio(dpr * scale);

    // (re)build post chain
    this.composer?.dispose();
    this.composer = null;
    this.bloomPass = null;
    if (preset !== 'performance') {
      this.composer = new EffectComposer(this.renderer);
      this.composer.addPass(new RenderPass(this.scene, this.chase.camera));
      this.bloomPass = new UnrealBloomPass(
        new THREE.Vector2(window.innerWidth / 2, window.innerHeight / 2),
        preset === 'quality' ? 0.75 : 0.55,
        0.55,
        0.82
      );
      this.composer.addPass(this.bloomPass);
      this.composer.addPass(new OutputPass());
    }
    this.resize();
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.composer?.setSize(w, h);
    this.chase.resize(w / h);
  }

  /** Build the world for a race. */
  loadRace(view: RaceView): void {
    this.unloadRace();
    const track = view.track;
    this.currentTrack = track;
    const env = getEnvironment(track.def.environment);

    this.scene.fog = new THREE.FogExp2(env.fogColor, env.fogDensity * (this.settings.graphicsPreset === 'performance' ? 1.3 : 1));
    this.renderer.setClearColor(env.fogColor);

    this.environment = env.build(this.scene, track, this.settings.graphicsPreset);
    this.trackHandle = buildTrackMeshes(track, {
      edgeColor: env.edgeColor,
      edgeColorAlt: this.settings.colorAssist ? 0xffffff : env.edgeColorAlt,
      wallColor: env.wallColor,
      colorAssist: this.settings.colorAssist,
    });
    this.scene.add(this.trackHandle.group);

    this.particles = new ParticleSystem(this.scene, this.settings.effectIntensity);
    this.streaks = new SpeedStreaks(this.scene);

    for (const sv of view.ships) {
      const vehicle = getVehicle(sv.info.vehicleId);
      const handle = buildShip(vehicle, sv.info.paintId);
      this.scene.add(handle.group);
      this.ships.push(handle);
      this.shipFx.push(new ShipEffects(this.scene, handle.engines, handle.glowMat.color.getHex()));
    }

    // ghost ship: translucent copy of the player's vehicle
    if (view.ghost && view.localIndex >= 0) {
      const vehicle = getVehicle(view.ships[view.localIndex].info.vehicleId);
      this.ghostShip = buildShip(vehicle, view.ships[view.localIndex].info.paintId);
      this.ghostShip.group.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          const mat = (o.material as THREE.Material).clone();
          mat.transparent = true;
          mat.opacity = 0.32;
          mat.depthWrite = false;
          o.material = mat;
        }
      });
      this.scene.add(this.ghostShip.group);
    }

    if (view.localIndex >= 0) this.chase.snapTo(view.ships[view.localIndex]);
    else if (view.ships.length) this.chase.snapTo(view.ships[0]);
  }

  unloadRace(): void {
    this.trackHandle?.dispose();
    this.trackHandle = null;
    this.environment?.dispose();
    this.environment = null;
    for (const s of this.ships) s.dispose();
    this.ships = [];
    for (const fx of this.shipFx) fx.dispose();
    this.shipFx = [];
    this.particles?.dispose();
    this.particles = null;
    this.streaks?.dispose();
    this.streaks = null;
    this.ghostShip?.dispose();
    this.ghostShip = null;
    this.currentTrack = null;
    this.scene.fog = null;
  }

  addShake(v: number): void {
    this.chase.addShake(v);
  }

  setCameraMode(mode: 'chase' | 'orbit' | 'podium'): void {
    this.chase.setMode(mode);
  }

  /** Render one frame of a race. */
  renderRace(view: RaceView, dt: number, lookBack: boolean, spectateIndex = 0): void {
    const start = performance.now();
    this.time += dt;

    // camera target — an eliminated local player spectates the leader
    let camShip: ShipView | undefined =
      view.localIndex >= 0
        ? view.ships[view.localIndex]
        : view.ships[view.standings[spectateIndex] ?? 0] ?? view.ships[0];
    if (camShip && camShip.eliminated && !camShip.finished) {
      camShip = view.ships[view.standings[0] ?? 0] ?? camShip;
    }
    if (camShip) {
      const speedNorm = Math.min(1, camShip.speed / 105);
      this.chase.update(camShip, dt, lookBack, speedNorm);
    }

    // ships
    for (let i = 0; i < this.ships.length; i++) {
      const handle = this.ships[i];
      const sv = view.ships[i];
      if (!sv) continue;
      handle.group.visible = sv.visible;
      handle.group.position.set(sv.pos.x, sv.pos.y, sv.pos.z);
      handle.group.quaternion.set(sv.quat.x, sv.quat.y, sv.quat.z, sv.quat.w);
      // visual banking lean from steering
      // bank INTO the turn (ship local +X points to world-left per the basis quat,
      // so a positive roll for steer>0 dips the true-right side)
      handle.group.rotateZ(sv.steer * 0.34);
      // respawn blink
      if (sv.respawning) {
        handle.group.visible = Math.sin(this.time * 20) > -0.4;
      }
      handle.setDamageFlash(sv.wallContact);
      if (this.particles) this.shipFx[i]?.update(sv, this.particles, dt);
    }

    // ghost
    if (this.ghostShip && view.ghost) {
      this.ghostShip.group.visible = view.ghost.visible;
      if (view.ghost.visible) {
        this.ghostShip.group.position.set(view.ghost.pos.x, view.ghost.pos.y, view.ghost.pos.z);
        this.ghostShip.group.quaternion.set(
          view.ghost.quat.x,
          view.ghost.quat.y,
          view.ghost.quat.z,
          view.ghost.quat.w
        );
      }
    }

    // systems
    this.particles?.update(dt);
    this.trackHandle?.update(dt, this.time);
    this.environment?.update(dt, this.chase.camera.position, this.time);
    if (this.streaks && camShip) {
      const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.chase.camera.quaternion);
      this.streaks.update(
        this.chase.camera.position,
        this.chase.camera.quaternion,
        fwd,
        Math.min(1, camShip.speed / 105) * (this.settings.reducedMotion ? 0.4 : 1),
        camShip.boosting
      );
    }

    this.renderFrame();
    this.collectStats(start, view.ships.length);
  }

  /** Render menu background (environment only, slow orbit). */
  renderBackdrop(dt: number): void {
    const start = performance.now();
    this.time += dt;
    this.environment?.update(dt, this.chase.camera.position, this.time);
    this.trackHandle?.update(dt, this.time);
    this.renderFrame();
    this.collectStats(start, 0);
  }

  private renderFrame(): void {
    this.renderer.info.reset();
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.chase.camera);
  }

  private collectStats(start: number, entities: number): void {
    const ms = performance.now() - start;
    this.frameTimes.push(ms);
    if (this.frameTimes.length > 30) this.frameTimes.shift();
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    this.stats.frameMs = avg;
    this.stats.drawCalls = this.renderer.info.render.calls;
    this.stats.triangles = this.renderer.info.render.triangles;
    this.stats.entities = entities;
  }

  setFps(fps: number): void {
    this.stats.fps = fps;
  }

  dispose(): void {
    this.unloadRace();
    this.composer?.dispose();
    this.renderer.dispose();
  }
}
